import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { grid, gridColumnsRoot, gridColumnsTablet } from '../../../utils/grid'
import React, { FunctionComponent } from 'react'
import styled, { css, SimpleInterpolation } from 'styled-components'
import Text, { TextProps } from '../text/Text'
import { ImageNodeData } from '../../../contexts/images/ImagesContext'
import Img from 'gatsby-image'
import { mediaQueries } from '../../../utils/mediaQueries'
import { rem } from '../../../utils/rem'
import { spacing } from '../../../utils/spacing'
import { spacings } from '../../../tokens/Spacings'
import useImageData from '../../../hooks/use-image-data/useImageData'
import { imageAnimation } from './animations'

type TextWithImageImageAlignments = 'left' | 'right'

export interface TextWithImageProps extends TextProps {
  image: string
  imageAlignment?: TextWithImageImageAlignments
}

const TextWithImage: FunctionComponent<TextWithImageProps> = ({
  className,
  image,
  imageAlignment = 'right',
  lead,
  link,
  text,
  date,
  title,
  size = 'medium',
}) => {
  const imageData = useImageData(`text-with-image/${image}`) as ImageNodeData

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  return (
    <StyledTextWithImage className={className} ref={ref} size={size}>
      {imageData && (
        <ImageWrapper imageAlignment={imageAlignment} size={size}>
          <ImageInner
            animate={inView ? ['opacityIn', 'transformIn'] : 'initial'}
            variants={imageAnimation}
          >
            <Img alt={title} fluid={imageData.fluid} />
          </ImageInner>
        </ImageWrapper>
      )}
      <TextWrapper imageAlignment={imageAlignment} size={size}>
        <Text
          animateDelay={0.2}
          animateIn={inView}
          date={date}
          lead={lead}
          link={link}
          size={size}
          text={text}
          title={title}
        />
      </TextWrapper>
    </StyledTextWithImage>
  )
}

interface StyledTextWithImageProps {
  size: 'medium' | 'small'
}

const StyledTextWithImage = styled.div<StyledTextWithImageProps>`
  ${grid()};
  ${spacing()};
  align-items: center;

  ${({ size }): SimpleInterpolation =>
    size === 'small'
      ? css`
          margin-bottom: ${rem(96)};
          ${mediaQueries.tablet} {
            margin-bottom: ${rem(60)};
          }
        `
      : ''};
`

interface ImageProps {
  imageAlignment: TextWithImageImageAlignments
  size: 'medium' | 'small'
}

const ImageInner = styled(motion.div)`
  overflow: hidden;
`

const getImageWrapperGridColumn = (size: string, alignment: string): string => {
  let start
  switch (size) {
    case 'small':
      start = alignment === 'left' ? 2 : 9
      return `${start} / span 5`

    default:
      start = alignment === 'left' ? 1 : 8
      return `${start} / span 9`
  }
}

const getTextWrapperGridColumn = (size: string, alignment: string): string => {
  let start
  switch (size) {
    case 'small':
      start = alignment === 'left' ? 8 : 2
      return `${start} / span 6`

    default:
      start = alignment === 'left' ? 11 : 2
      return `${start} / span 5`
  }
}

const ImageWrapper = styled.div<ImageProps>`
  display: block;
  margin-bottom: ${rem(spacings.small.root)};
  width: calc(100% + ${rem(spacings.side.root)});
  grid-column: 1 / span ${gridColumnsRoot};
  overflow: hidden;

  ${mediaQueries.tablet} {
    margin-bottom: ${rem(spacings.small.tablet)};
    width: calc(100% + ${rem(spacings.side.tablet)});
    grid-column: 1 / span ${gridColumnsTablet};
  }

  ${mediaQueries.desktop} {
    width: 100%;
    grid-row: 1;
  }

  ${({ imageAlignment, size }): SimpleInterpolation =>
    imageAlignment === 'left' &&
    css`
      margin-left: -${rem(spacings.side.root)};

      ${mediaQueries.tablet} {
        margin-left: -${rem(spacings.side.tablet)};
      }

      ${mediaQueries.desktop} {
        margin: 0;
        grid-column: ${getImageWrapperGridColumn(size, imageAlignment)};
      }
    `}

  ${({ imageAlignment, size }): SimpleInterpolation =>
    imageAlignment === 'right' &&
    css`
      margin-right: -${rem(spacings.side.root)};

      ${mediaQueries.tablet} {
        margin-right: -${rem(spacings.side.tablet)};
      }

      ${mediaQueries.desktop} {
        margin: 0;
        grid-column: ${getImageWrapperGridColumn(size, imageAlignment)};
      }
    `}
`

type TextWrapperProps = ImageProps

const TextWrapper = styled.div<TextWrapperProps>`
  grid-column: 1 / span ${gridColumnsRoot};

  ${mediaQueries.desktop} {
    grid-row: 1;
  }

  ${({ imageAlignment, size }): SimpleInterpolation =>
    imageAlignment === 'left'
      ? css`
          ${mediaQueries.tablet} {
            grid-column: 1 / span 5;
          }

          ${mediaQueries.desktop} {
            grid-column: ${getTextWrapperGridColumn(size, imageAlignment)};
          }
        `
      : ''}

  ${({ imageAlignment, size }): SimpleInterpolation =>
    imageAlignment === 'right'
      ? css`
          ${mediaQueries.tablet} {
            grid-column: 4 / span 5;
          }

          ${mediaQueries.desktop} {
            grid-column: ${getTextWrapperGridColumn(size, imageAlignment)};
          }
        `
      : ''};
`

export default TextWithImage
